// The ranking — every candidate, and No Confidence, in vote order.
//
// **No Confidence is a row in this table, not a footnote.** It is a contender,
// not a spoiled paper (SO 15.7, position.ts:5-11): it ranks with the candidates,
// everything below it is refused, and when it wins the place is UNFILLED and
// goes to Standing Committee — the runner-up is not elected. A table that hid it
// among the paper tallies would make the shortfall below look like a clerical
// accident instead of a decision of the electorate.
//
// The ORDER here is display order and nothing else. Votes descending, and where
// two are level the engine's own order (id ascending, No Confidence last). It
// never decides a seat: equal votes at a cut line are reported as a TIE before
// any ordering is consulted (position.ts:24-26), which is what the withheld card
// above this table exists to say.

import type { JSX } from 'react';
import type { PositionItemResult } from '@cca/decides-count';

interface CandidateTableProps {
  result: PositionItemResult;
  candidateNames: Record<string, string>;
}

interface Row {
  key: string;
  name: string;
  votes: number;
  outcome: string;
  noConfidence: boolean;
}

export function CandidateTable({ result, candidateNames }: CandidateTableProps): JSX.Element {
  const rows: Row[] = [
    ...result.totals.map((total) => ({
      key: total.candidateId,
      name: candidateNames[total.candidateId] ?? total.candidateId,
      votes: total.votes,
      outcome: outcomeFor(result, total.candidateId),
      noConfidence: false,
    })),
    {
      key: 'no-confidence',
      name: 'No Confidence',
      votes: result.noConfidence,
      outcome: 'Refuses the places below it (SO 15.7)',
      noConfidence: true,
    },
  ].sort((a, b) => b.votes - a.votes || rankNoConfidenceLast(a, b));

  return (
    <table className="table result-table">
      <caption className="result-table-caption">
        Every candidate, and No Confidence, in vote order.
      </caption>
      <thead>
        <tr>
          <th scope="col">
            <span className="ballot-sr">Who was voted for</span>
          </th>
          <th scope="col" className="num">
            Votes
          </th>
          <th scope="col">Outcome</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            {/* The one cell on this screen whose content is UNBOUNDED. Every
                other row label is a word this package chose; a candidate's name
                arrives from the roll and nobody can promise it is short. It gets
                its own class because it needs the opposite treatment from the
                labels — see `.result-candidate` in result.css. */}
            <th scope="row" className="result-candidate">
              {row.name}
            </th>
            <td className="num">{row.votes}</td>
            <td className="result-outcome">{row.outcome}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * What happened to one candidate.
 *
 * The third branch is not defensive padding. When the ALTERNATE cut is tied and
 * unresolved, a candidate is in `elected` — they certainly hold a place — but in
 * neither `substantive` nor `alternates`, because which of them is the alternate
 * is not settled by the votes (types.ts:594-607, "That is the honest report, not
 * a defect"). Falling through to "Not elected" there would unseat someone the
 * electorate elected.
 */
function outcomeFor(result: PositionItemResult, candidateId: string): string {
  if (result.substantive.includes(candidateId)) return 'Elected';
  if (result.alternates.includes(candidateId)) return 'Elected — alternate';
  if (result.elected.includes(candidateId)) return 'Elected — which place is not yet settled';
  return 'Not elected';
}

function rankNoConfidenceLast(a: Row, b: Row): number {
  if (a.noConfidence === b.noConfidence) return 0;
  return a.noConfidence ? 1 : -1;
}
